/**
 * visual-game 用户端、编辑端和运营端共享的本地 token store，
 * 由 deviceKey/devicePrefix 区分各应用的设备标识。
 *
 * 为何不接 `@drama/auth-client`：vg 的登录由页面 composable 直接驱动——`api.auth.login/register`
 * 拿到 token 后手动 `tokenStore.setTokens`，登出手动 `clear`，匿名合流读 `getDeviceId()` 当
 * anonymousId，没有 auth-client 的响应式 session / 路由守卫 / flows 那套。这与 auth-client
 * 「session 唯一归属」的模型不兼容，重接需改页面层(超出本次边界)，故按任务回退合并成一份。
 *
 * refresh 请求由调用端注入，token store 只负责持久化和轮转，不自行发送网络请求。
 */
import { visualGameAccessNeedsRefresh } from './vg-access-token.js'
import { newVisualGameSessionEpoch, refreshSessionChanged, refreshSessionTransition, withVisualGameRefreshLock } from './vg-token-refresh.js'

const ACCESS_KEY = 'visualGame.accessToken'
const REFRESH_KEY = 'visualGame.refreshToken'
const SESSION_EPOCH_KEY = 'visualGame.sessionEpoch'
const SESSION_TOKENS_PREFIX = 'visualGame.sessionTokens:'
const SESSION_PUBLISHED_PREFIX = 'visualGame.sessionPublished:'
const REFRESH_ATTEMPT_PREFIX = 'visualGame.refreshAttempt:'

function refreshOutcomeUnknown() {
  const error = new Error('auth_refresh_outcome_unknown: sign in again to recover safely')
  error.code = 'AUTH_REFRESH_OUTCOME_UNKNOWN'
  return error
}

async function refreshAttemptKey(refresh_token) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(refresh_token))
  return REFRESH_ATTEMPT_PREFIX + Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

function read(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key, value) {
  try {
    if (value == null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
    return localStorage.getItem(key) === (value == null ? null : value)
  } catch {
    return false
  }
}

function stableDeviceId(deviceKey, prefix) {
  const current = read(deviceKey)
  if (current) return current
  const next = `${prefix}${Math.random().toString(36).slice(2)}`
  write(deviceKey, next)
  return next
}

/**
 * @param {object} [opts]
 * @param {(request: { refresh_token: string }) => Promise<{ access_token: string, refresh_token: string }>} opts.refreshSession
 * @param {string} [opts.deviceKey] deviceId 的 storage key(vg 与 vg-studio 各用一个，互不覆盖)
 * @param {string} [opts.devicePrefix] 新 deviceId 前缀
 */
export function createLocalTokenStore({
  refreshSession,
  deviceKey = 'visualGame.deviceId',
  devicePrefix = 'vg-dev-',
} = {}) {
  const deviceId = stableDeviceId(deviceKey, devicePrefix)
  const inflight = new Map()

  function snapshot() {
    const epoch = read(SESSION_EPOCH_KEY)
    if (epoch === null) return { epoch, access_token: read(ACCESS_KEY), refresh_token: read(REFRESH_KEY) }
    let tokens
    try { tokens = JSON.parse(read(SESSION_TOKENS_PREFIX + epoch) || '{}') } catch { tokens = {} }
    return { epoch, access_token: typeof tokens?.access_token === 'string' ? tokens.access_token || null : null,
      refresh_token: typeof tokens?.refresh_token === 'string' ? tokens.refresh_token || null : null }
  }

  function writeTokens(tokens, epoch) {
    if (epoch === null) {
      const access_saved = write(ACCESS_KEY, tokens.access_token || null)
      const refresh_saved = write(REFRESH_KEY, tokens.refresh_token || null)
      return access_saved && refresh_saved
    }
    return write(SESSION_TOKENS_PREFIX + epoch, JSON.stringify({ access_token: tokens.access_token || null, refresh_token: tokens.refresh_token || null }))
  }

  function discardTokens(epoch) {
    if (epoch === null) {
      write(ACCESS_KEY, null)
      write(REFRESH_KEY, null)
    } else {
      write(SESSION_TOKENS_PREFIX + epoch, null)
      write(SESSION_PUBLISHED_PREFIX + epoch, null)
    }
  }

  function discardReplacedSessions() {
    const published = []
    try {
      for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index)
        if (key?.startsWith(SESSION_PUBLISHED_PREFIX)) published.push(key.slice(SESSION_PUBLISHED_PREFIX.length))
      }
    } catch { return }
    // Only previously published epochs are eligible. A concurrent login may have written
    // its token record without publishing its pointer yet; that record must be left alone.
    for (const epoch of published) {
      if (read(SESSION_EPOCH_KEY) !== epoch) discardTokens(epoch)
    }
  }

  function replaceSession(tokens) {
    const previous = read(SESSION_EPOCH_KEY)
    const epoch = newVisualGameSessionEpoch()
    // Publish a complete pair atomically. A late refresh can only write its old epoch's record.
    writeTokens(tokens, epoch)
    write(SESSION_EPOCH_KEY, epoch)
    // Keep this marker separate so marking publication cannot overwrite a completed refresh.
    write(SESSION_PUBLISHED_PREFIX + epoch, '1')
    discardTokens(previous)
    discardReplacedSessions()
  }

  // token 每次直接读 localStorage、不在内存缓存：第三方登录的授权小窗写入的 token，主页面才看得到
  function setTokens(tokens = {}) {
    replaceSession(tokens)
  }

  function clear() {
    replaceSession({})
  }

  async function rotateSession(observed) {
    let current = snapshot()
    const before = refreshSessionTransition(observed, current)
    if (before === 'session_changed') throw refreshSessionChanged()
    if (before === 'rotated') return current.access_token
    if (!observed.refresh_token) throw new Error('No refresh token')
    // Web Lock 会随文档关闭而释放，但服务器可能仍已消费 refresh。
    // 发送前持久记录一次性尝试；没有成功发布新 pair 时永不自动重交旧凭据。
    const attempt_key = await refreshAttemptKey(observed.refresh_token)
    current = snapshot()
    const prepared = refreshSessionTransition(observed, current)
    if (prepared === 'session_changed') throw refreshSessionChanged()
    if (prepared === 'rotated') return current.access_token
    if (read(attempt_key) !== null) throw refreshOutcomeUnknown()
    if (!write(attempt_key, JSON.stringify({ epoch: observed.epoch }))) throw refreshOutcomeUnknown()
    let response
    try {
      response = await refreshSession({ refresh_token: observed.refresh_token })
    } catch (error) {
      current = snapshot()
      const after = refreshSessionTransition(observed, current)
      if (after === 'session_changed') throw refreshSessionChanged()
      if (after === 'rotated') return current.access_token
      // 明确的鉴权拒绝仍由 SDK 处理；断网/中断不能推断服务器未消费。
      if (error?.status === 401 || error?.statusCode === 401) throw error
      throw refreshOutcomeUnknown()
    }
    current = snapshot()
    const after = refreshSessionTransition(observed, current)
    if (after === 'session_changed') throw refreshSessionChanged()
    if (after === 'rotated') return current.access_token
    if (!response?.access_token || !response?.refresh_token || response.refresh_token === observed.refresh_token) throw refreshOutcomeUnknown()
    // 内部轮转保留 epoch，且必须确认发布成功后才清除在途标记。
    if (!writeTokens(response, observed.epoch)) throw refreshOutcomeUnknown()
    current = snapshot()
    if (current.epoch !== observed.epoch) throw refreshSessionChanged()
    if (current.refresh_token !== response.refresh_token || current.access_token !== response.access_token) throw refreshOutcomeUnknown()
    write(attempt_key, null)
    return current.access_token
  }

  async function rotate(observed) {
    try { return await rotateSession(observed) }
    finally {
      // A response may settle after login/logout already removed this record.
      if (read(SESSION_EPOCH_KEY) !== observed.epoch) discardTokens(observed.epoch)
    }
  }

  function refresh({ access_token } = {}) {
    const observed = snapshot()
    // 绑定原 401 所用 access，避免在 await/入锁前轮转完成后又刷新新 pair。
    if (access_token !== undefined && access_token !== observed.access_token) return Promise.resolve(observed.access_token)
    if (inflight.has(observed.epoch)) return inflight.get(observed.epoch)
    const pending = withVisualGameRefreshLock(() => rotate(observed)).finally(() => {
      if (inflight.get(observed.epoch) === pending) inflight.delete(observed.epoch)
    })
    inflight.set(observed.epoch, pending)
    return pending
  }

  async function getValidAccessToken() {
    const current = snapshot()
    if (!current.refresh_token || !visualGameAccessNeedsRefresh(current.access_token)) return current.access_token
    return refresh()
  }

  return {
    getValidAccessToken,
    getAccessToken: () => snapshot().access_token,
    getRefreshToken: () => snapshot().refresh_token,
    getSessionEpoch: () => read(SESSION_EPOCH_KEY),
    getDeviceId: () => deviceId,
    hasSession: () => {
      const current = snapshot()
      return current.access_token !== null || current.refresh_token !== null
    },
    setTokens,
    clear,
    refresh,
  }
}
